import { readConsentCookie } from '$lib/consent';
import { trackMarketingEvent } from '$lib/marketing';

export interface AnalyticsOptions {
	eventId?: string;
	props?: Record<string, string | number | boolean>;
	revenue?: { currency: string; amount: string };
	ecommerce?: {
		currency: string;
		value: number;
		transaction_id?: string;
		items?: Array<{
			item_id: string;
			item_name: string;
			price: number;
			quantity: number;
		}>;
	};
	interactive?: boolean;
}

interface PlausibleFunction {
	(name: string, options?: AnalyticsOptions): void;
	q?: unknown[][];
}

declare global {
	interface Window {
		plausible?: PlausibleFunction;
		dataLayer?: Record<string, unknown>[];
		gtag?: (...args: unknown[]) => void;
	}
}

let consentInitialised = false;

export function updateGoogleConsent(): void {
	initialiseAnalyticsQueues();
	window.gtag ??= function () {
		window.dataLayer?.push(arguments as unknown as Record<string, unknown>);
	};
	if (!consentInitialised) {
		window.gtag('consent', 'default', {
			analytics_storage: 'denied',
			ad_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied'
		});
		window.gtag('set', 'ads_data_redaction', true);
		consentInitialised = true;
	}
	const consent = readConsentCookie();
	window.gtag('consent', 'update', {
		analytics_storage: consent?.analytics ? 'granted' : 'denied',
		ad_storage: consent?.advertising ? 'granted' : 'denied',
		ad_user_data: consent?.advertising ? 'granted' : 'denied',
		ad_personalization: consent?.advertising ? 'granted' : 'denied'
	});
}

export function initialiseAnalyticsQueues(): void {
	window.dataLayer ??= [];
	if (!window.plausible) {
		const plausible: PlausibleFunction = (...args) => {
			plausible.q ??= [];
			plausible.q.push(args);
		};
		window.plausible = plausible;
	}
}

/**
 * Internal-traffic opt-out. Plausible's own script already honours
 * `localStorage.plausible_ignore = "true"`; checking the same flag here keeps
 * our test submissions out of GTM/GA4 too. Set it once per browser you test in.
 */
function isInternalBrowser(): boolean {
	try {
		return localStorage.getItem('plausible_ignore') === 'true';
	} catch {
		return false;
	}
}

/** Emits no event unless the visitor explicitly accepted analytics. */
export function trackEvent(name: string, options: AnalyticsOptions = {}, gtmEvent?: string): void {
	if (!readConsentCookie()?.analytics || isInternalBrowser()) return;
	const eventName = gtmEvent ?? name;
	const metaEvent =
		eventName === 'generate_lead' || eventName === 'demo_request'
			? 'Lead'
			: eventName === 'purchase'
				? 'Purchase'
				: eventName === 'begin_checkout'
					? 'InitiateCheckout'
					: null;
	if (
		metaEvent &&
		(options.eventId || eventName === 'purchase' || eventName === 'begin_checkout')
	) {
		trackMarketingEvent(
			metaEvent,
			options.eventId ??
				(eventName === 'purchase'
					? `purchase_${options.ecommerce?.transaction_id}`
					: crypto.randomUUID()),
			{
				content_name: options.props?.lead_type === 'demo_request' ? 'Workflow demo' : 'PPF Engine',
				...(options.ecommerce
					? { currency: options.ecommerce.currency, value: options.ecommerce.value }
					: {})
			}
		);
	}
	initialiseAnalyticsQueues();
	const { ecommerce, eventId, ...plausibleOptions } = options;
	void eventId;
	window.plausible?.(name, plausibleOptions);
	window.dataLayer?.push({ ecommerce: null });
	window.dataLayer?.push({
		event: gtmEvent ?? name,
		...(options.props ?? {}),
		...(ecommerce
			? { ecommerce }
			: options.revenue
				? {
						ecommerce: {
							transaction_id: options.props?.transaction_id,
							value: options.revenue.amount,
							currency: options.revenue.currency
						}
					}
				: {})
	});
}

export function trackEventOnce(
	key: string,
	name: string,
	options: AnalyticsOptions = {},
	gtmEvent?: string
): void {
	if (!readConsentCookie()?.analytics) return;
	const storageKey = `ppfe_event_${key}`;
	try {
		if (sessionStorage.getItem(storageKey)) return;
		trackEvent(name, options, gtmEvent);
		sessionStorage.setItem(storageKey, '1');
	} catch {
		trackEvent(name, options, gtmEvent);
	}
}
